import type { ReactNode } from 'react';
import React, { createContext } from 'react';

import type { DisplayMode } from 'src/components';
import type { Apollo } from 'src/gql/generated';

import { OrphanResolutionDialogs } from './OrphanResolutionDialogs';

/** Context to share display mode with cell renderers */
export const DocumentGridDisplayModeContext = createContext<DisplayMode>('flat');
/** Context to share reportId with cell renderers */
export const DocumentGridReportIdContext = createContext<UUID | null>(null);
/** Matches `useReportDataSource` trashed flag for scoped queries (e.g. file-type filter options). */
export const DocumentGridTrashedContext = createContext<boolean>(false);
/** Matches `useReportDataSource` docVariant for scoped queries (e.g. file-type filter options). */
export const DocumentGridDocVariantContext = createContext<Apollo.DocumentVariant | undefined>(
  undefined
);
/** Context to indicate the grid is filtered to unconfirmed relationships only. */
export const DocumentGridUnconfirmedOnlyContext = createContext<boolean>(false);
/** Context to indicate the grid is filtered to missing matches only. */
export const DocumentGridMissingMatchesOnlyContext = createContext<boolean>(false);

interface DocumentGridContextProvidersProps {
  children: ReactNode;
  displayMode: DisplayMode;
  missingMatchesOnly: boolean;
  reportId: UUID;
  unconfirmedRelationshipsOnly: boolean;
  docVariant?: Apollo.DocumentVariant;
  trashed?: boolean;
}

/**
 * Provides shared state to `RepositoryDocumentNameCell` and its descendants
 * (relationship labels, action buttons, status chips, etc.) via React context.
 *
 * Cell renderers inside a DataGridPremium cannot receive props directly from the
 * parent component — they only get `GridRenderCellParams`. These contexts bridge
 * that gap by exposing display mode, report ID, and filter state that the cell
 * tree needs for relationship management.
 *
 * Wrap your `<DataGridPremium>` with this provider whenever the grid renders
 * `RepositoryDocumentNameCell` (used by both Repository and AI Reports).
 *
 * @example
 * ```tsx
 * <DocumentGridContextProviders
 *   displayMode={displayMode}
 *   missingMatchesOnly={false}
 *   reportId={reportId}
 *   unconfirmedRelationshipsOnly={false}
 * >
 *   <DataGridPremium ... />
 * </DocumentGridContextProviders>
 * ```
 */
export const DocumentGridContextProviders = React.memo(function DocumentGridContextProviders({
  children,
  displayMode,
  docVariant,
  missingMatchesOnly,
  reportId,
  trashed = false,
  unconfirmedRelationshipsOnly,
}: DocumentGridContextProvidersProps): JSX.Element {
  return (
    <DocumentGridReportIdContext.Provider value={reportId}>
      <DocumentGridTrashedContext.Provider value={trashed}>
        <DocumentGridDocVariantContext.Provider value={docVariant}>
          <DocumentGridDisplayModeContext.Provider value={displayMode}>
            <DocumentGridUnconfirmedOnlyContext.Provider value={unconfirmedRelationshipsOnly}>
              <DocumentGridMissingMatchesOnlyContext.Provider value={missingMatchesOnly}>
                {children}
                <OrphanResolutionDialogs />
              </DocumentGridMissingMatchesOnlyContext.Provider>
            </DocumentGridUnconfirmedOnlyContext.Provider>
          </DocumentGridDisplayModeContext.Provider>
        </DocumentGridDocVariantContext.Provider>
      </DocumentGridTrashedContext.Provider>
    </DocumentGridReportIdContext.Provider>
  );
});
